import { Socket } from "phoenix"

// Socket Echo /chat/ws (czat, powiadomienia na żywo, lista zadań) z odświeżanym podpisem.
// Rails wstawia parametry do strony przy renderze, a podpis checksum2 wygasa po 24 h
// (Echo::SocketSignature). Parametry idą do Phoenixa jako funkcja - każde ponowne łączenie
// (deploy Echo, przerwa w sieci, uśpiony laptop) bierze aktualne. Świeże pobieramy
// z /account/notifications/echo_socket.json, gdy do wygaśnięcia zostało mniej niż godzina:
// po powrocie do karty i po błędzie połączenia - jak space_signature w widgecie webchatu.

export const REFRESH_URL = "/account/notifications/echo_socket.json"
export const REFRESH_BEFORE_MS = 60 * 60 * 1000
// odrzucone połączenie Phoenix ponawia co kilka sekund - pobieramy najwyżej raz na minutę
export const MIN_REFRESH_INTERVAL_MS = 60 * 1000

// Wspólne parametry socketów jednej paczki JS - każda paczka (application, live-notifier, chat-dock,
// chat-panel, chat-secure) ma własną kopię modułu, więc odświeża się osobno
export function createParamsStore(initial, { fetchFn = (...args) => fetch(...args), now = () => Date.now() } = {}) {
  let params = { ...initial }
  let lastRefreshAt = null

  // stary format (bez expires_at) nie wygasa - nie ma czego odświeżać
  const expiresSoon = () => {
    if (!params.expires_at) return false
    return Number(params.expires_at) * 1000 - now() < REFRESH_BEFORE_MS
  }

  // Rails nie wyda świeżych parametrów (204 - live wyłączone, 401 - wylogowany): zostają stare
  const refresh = async () => {
    if (lastRefreshAt !== null && now() - lastRefreshAt < MIN_REFRESH_INTERVAL_MS) return
    lastRefreshAt = now()
    try {
      const response = await fetchFn(REFRESH_URL, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (response.status !== 200) return
      const fresh = await response.json()
      // inny user (wylogowanie i logowanie w innej karcie) - tematy kanałów są z renderu, zostają stare
      const sameUser = String(fresh?.user_id) === String(params.user_id) && String(fresh?.account_id) === String(params.account_id)
      if (fresh && fresh.expires_at && sameUser) params = fresh
    } catch {
      // sieć - kolejna próba przy następnym błędzie połączenia albo powrocie do karty
    }
  }

  return {
    get: () => params,
    // świeższe parametry z kolejnego renderu (np. drugi komponent na stronie) wygrywają
    offer: (other) => {
      if (Number(other?.expires_at || 0) > Number(params.expires_at || 0)) params = { ...other }
    },
    refreshIfExpiring: () => (expiresSoon() ? refresh() : Promise.resolve()),
  }
}

let store = null

function sharedStore(initial) {
  if (store) {
    store.offer(initial)
    return store
  }
  store = createParamsStore(initial)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") store.refreshIfExpiring()
  })
  return store
}

// new Socket(...) z odświeżanym podpisem - echo to config.echo z Chat::EchoPublisher.socket_config
export function createEchoSocket(echo) {
  const params = sharedStore(echo.params)
  const socket = new Socket(`${echo.origin}${echo.path}`, { params: () => params.get() })
  // odrzucony connect (np. wygasły podpis) Phoenix zgłasza jako błąd i sam ponawia z params()
  socket.onError(() => params.refreshIfExpiring())
  return socket
}
